"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import Navbar from "@/components/layout/navbar/Navbar";
import Breadcrumb from "@/components/Common/Breadcrumb";
import Button from "@/components/ui/Button";
import ContactModal from "../Home/ContactModal";

interface HeroProps {
  categoryName: string;
}
// Hero images excluding bg-0
const HERO_IMAGES = Array.from(
  { length: 5 },
  (_, i) => `/assets/formations-hero-bg-${i + 1}.svg`,
);

const Hero: React.FC<HeroProps> = ({ categoryName }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isNosFormations = categoryName.toLowerCase() === "nos formations";

  // Stable random collapse states
  const [collapseStates, setCollapseStates] = useState<boolean[]>(
    HERO_IMAGES.map(() => false),
  );

  useEffect(() => {
    if (!isNosFormations) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCollapseStates(HERO_IMAGES.map(() => Math.random() > 0.5));
    }
  }, [isNosFormations]);

  const isAllFormations = categoryName.toLowerCase() === "nos formations";

  return (
    <section className="relative w-full sm:min-h-88 bg-[#1B365E] flex flex-col items-center justify-start">
      {/* Hero Background / Images */}
      <div className="absolute inset-0 z-0">
        {isNosFormations ? (
          <Image
            src="/assets/formations-hero-bg-0.svg"
            alt="Hero Background"
            fill
            priority
            className="object-cover w-full h-full"
          />
        ) : (
          HERO_IMAGES.map((src, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-all duration-500 ${
                collapseStates[i]
                  ? "scale-0 opacity-0"
                  : "scale-100 opacity-100"
              }`}
            >
              <Image
                src={src}
                alt={`Hero Image ${i + 1}`}
                fill
                priority
                className="object-cover w-full h-full"
              />
            </div>
          ))
        )}
        <div
          className="absolute inset-0 bg-[#1B365E]/55 sm:hidden"
          aria-hidden="true"
        />
      </div>

      {/* Navbar */}
      <div className="relative z-100 w-full">
        <Navbar />
      </div>

      {/* Hero Text */}
      <div
        className={`relative z-10 px-4 xl:px-10 pt-13.5 sm:pt-22 w-full mx-auto ${
          isAllFormations ? "mb-8" : "mb-10 sm:mb-6.5"
        }`}
      >
        <Breadcrumb currentLabel={categoryName} />
        <h1 className="font-red-hat font-light text-[30px] sm:text-[40px] md:text-[48px] text-white leading-9.25 sm:leading-13 md:leading-15 tracking-[0.2px] mb-1.5 sm:mb-3">
          {!isNosFormations && (
            <>
              Formations en <br />
            </>
          )}
          {categoryName}
        </h1>
        <p className="text-white text-[14px] sm:text-[20px] leading-5 sm:leading-7.5 tracking-[0.2px] max-w-138 pt-8 sm:pt-0">
          {isNosFormations
            ? "Découvrez notre catalogue de formations professionnelles certifiantes, conçues pour développer vos compétences et booster votre carrière."
            : `Découvrez nos formations en ${categoryName}, certifiantes et adaptées à votre parcours professionnel.`}
        </p>
        <div className="mt-6 sm:mt-8 flex items-center sm:flex-row flex-col gap-6.5 sm:gap-8">
          <Button href="/quiz" target="_blank" rel="noopener noreferrer" variant="primary" className="w-full sm:w-fit">
            Se laisser guider
          </Button>
          <Button
            variant="gradient"
            className="w-full sm:w-fit"
            onClick={() => setIsModalOpen(true)}
          >
            Nous contacter
          </Button>
        </div>
      </div>

      {/* Small logo at bottom */}
      <div className="flex items-center bg-white rounded-[40px] border-b border-[#40BFDE] py-3 px-4 justify-center absolute left-1/2 -translate-x-1/2 -bottom-5">
        <Image
          src="/assets/small-logo.svg"
          alt="small-logo"
          title="small-logo"
          width={30}
          height={17}
        />
      </div>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        formationTheme={isNosFormations ? undefined : categoryName}
        message={`Demande d'orientation depuis la page Formations en ${categoryName}.`}
      />
    </section>
  );
};

export default Hero;
